import { useCallback } from "react";
import { useSWRConfig } from "swr";
import { auth, hvoAuth, hvoFirestore, vceFirestore } from "../../configs/FirebaseConfig";
import { FIRESTORE_PRODUCTS_TABLE } from "../../constants/FirestoreConstant";
import { WAREHOUSE_FIELDS } from "../../constants/ProductSyncConstant";
import { buildWarehousePatch, detectStaleWrite, valuesEqualForProperty } from "../../functions/productSync";

/**
 * Writing product fields, to either shop's Firestore.
 *
 * Two things make this more than a `.update()`:
 *
 * 1. The old `cms/` is still in use and writes the *whole* product document. Everything here is
 *    therefore a targeted update of named fields only - never a document write - or a save here
 *    would wipe whatever was edited there. For the same reason every write is preceded by a
 *    re-read and a stale check: if `cms/` saved while this form was open, the user is told
 *    instead of quietly putting the old value back.
 *
 * 2. The HVO session can be missing. `loginToBothFirebase` swallows a failed HVO login and
 *    continues with VCE only, so a signed-in user is not necessarily allowed to write to HVO.
 *    Writes refuse with a Dutch message in that case, and `hasHvoSession()` lets the screen
 *    disable the fields up front rather than let someone type a value that cannot be saved.
 *
 * Expected refusals come back as `{ ok: false, reason, message }` rather than as exceptions -
 * they are outcomes the screen renders, not bugs. Firestore's own errors still throw.
 */
const resolveFirestore = (shopSource) => (shopSource === "HVO" ? hvoFirestore : vceFirestore);

/** Whether this session may write to the HVO project at all. */
export const hasHvoSession = () => !!hvoAuth.currentUser;

/** Whether a given shop's fields may be saved at all - what the screen disables its inputs on. */
export const canWriteToShop = (shopSource) => shopSource !== "HVO" || hasHvoSession();

const NO_HVO_SESSION_MESSAGE =
  "Geen HVO-sessie actief: je bent alleen bij Vleescentrum ingelogd. Log opnieuw in om velden van " +
  "Halalvlees Online op te slaan.";

const STALE_MESSAGE =
  "Dit product is ondertussen door iemand anders gewijzigd (waarschijnlijk in het oude CMS). " +
  "Ververs de gegevens en controleer de waarden voordat je opnieuw opslaat.";

const currentUserEmail = () => (auth.currentUser && auth.currentUser.email) || null;

const productDocument = (shopSource, productId) =>
  resolveFirestore(shopSource).collection(FIRESTORE_PRODUCTS_TABLE).doc(productId);

/** The fields a screen may hand in, in one place so the two write paths cannot disagree. */
export const EDITABLE_WAREHOUSE_FIELDS = WAREHOUSE_FIELDS;

export const useProductFields = () => {
  const { mutate: globalMutate } = useSWRConfig();

  // useProducts caches the merged both-shops list under "products"; after a write it is stale.
  const revalidateProducts = useCallback(() => globalMutate("products"), [globalMutate]);

  /**
   * Read the current stored values of some fields, straight from Firestore rather than from the
   * SWR cache. Used for the stale check, and by the screen to show what is really stored now.
   */
  const readFields = useCallback(async (shopSource, productId, fields = WAREHOUSE_FIELDS) => {
    const snapshot = await productDocument(shopSource, productId).get();
    if (!snapshot.exists) return null;
    const data = snapshot.data() || {};
    return fields.reduce((values, key) => ({ ...values, [key]: data[key] === undefined ? null : data[key] }), {});
  }, []);

  /**
   * The shared write path: check the session, re-read, refuse a stale write, then update only
   * the named fields.
   *
   * Deliberately NOT returned by the hook. It takes whatever patch it is handed, so it is the
   * one place that could write a field nobody decided was editable here - and this CMS shares
   * the product collection with `cms/`, where "editable here" is the whole promise. The named
   * write paths below own that decision: `updateWarehouseFields` through `buildWarehousePatch`,
   * `applySyncPlan` through the sync registry.
   *
   * Kept internal rather than given a gate of its own, because there is no single field list
   * that would be right for both. The registry (`isSyncableProperty`) answers "may this be
   * copied to the other shop", which is a different question from "may this be written here":
   * a phase 2 product editor legitimately writes `name` and `description` to one shop, and
   * those are on NEVER_SYNC precisely because they must not travel. Phase 2 therefore adds its
   * own named function here with its own field list, the same way phase 1 did, instead of
   * having to argue with a gate that means something else.
   *
   * `loadedValues` is what the form was populated with. Leaving it out skips the stale check,
   * which is only right when the caller knows there is nothing to lose.
   */
  const writeFields = useCallback(
    async ({ shopSource, productId, patch, loadedValues, fields }) => {
      if (!productId || !shopSource) {
        return { ok: false, reason: "invalid-target", message: "Geen product om op te slaan." };
      }
      if (shopSource === "HVO" && !hasHvoSession()) {
        return { ok: false, reason: "no-hvo-session", message: NO_HVO_SESSION_MESSAGE };
      }

      const keys = Object.keys(patch || {});
      if (keys.length === 0) {
        return { ok: true, changed: [], written: {}, noop: true };
      }

      const reference = productDocument(shopSource, productId);
      const snapshot = await reference.get();
      if (!snapshot.exists) {
        return {
          ok: false,
          reason: "not-found",
          message: "Dit product bestaat niet (meer) in deze webshop.",
        };
      }
      const stored = snapshot.data() || {};

      const stale = detectStaleWrite({
        loadedValues,
        currentValues: stored,
        fields: fields && fields.length > 0 ? fields : keys,
      });
      if (stale.length > 0) {
        return { ok: false, reason: "stale", message: STALE_MESSAGE, staleFields: stale };
      }

      // What this save really changes, reported back so the screen can show it and so the
      // caller knows whether it is worth asking about syncing to the other shop. Compared by
      // meaning, not by identity: rewriting the string "2400" as the number 2400 is a
      // normalisation, not something to tell the user about.
      const changed = keys
        .filter((key) => !valuesEqualForProperty(key, stored[key], patch[key]))
        .map((key) => ({ key, from: stored[key] === undefined ? null : stored[key], to: patch[key] }));

      await reference.update({
        ...patch,
        updatedAt: Date.now(),
        updatedBy: currentUserEmail(),
      });
      await revalidateProducts();

      return { ok: true, changed, written: patch };
    },
    [revalidateProducts],
  );

  /**
   * Save the warehouse fields on one product.
   *
   * Values are normalised first (Firestore holds gram values as both numbers and numeric
   * strings); a gram or pieces field filled in with something that is not a valid number is
   * refused rather than written as empty.
   */
  const updateWarehouseFields = useCallback(
    async ({ shopSource, productId, values, loadedValues }) => {
      const { patch, invalid } = buildWarehousePatch(values);
      if (invalid.length > 0) {
        return {
          ok: false,
          reason: "invalid",
          message: `Geen geldige waarde ingevuld bij: ${invalid.map((field) => field.label).join(", ")}.`,
          invalidFields: invalid,
        };
      }

      return writeFields({
        shopSource,
        productId,
        patch,
        loadedValues,
        fields: Object.keys(patch),
      });
    },
    [writeFields],
  );

  /**
   * Copy a changeset from `buildSyncPlan` onto the linked product in the other shop.
   *
   * The plan's `from` values are what the target held when the plan was built - typically read
   * from the cached products list, which can be minutes old. Using them as the loaded values
   * makes the stale check mean exactly the right thing: "the other product is still what I told
   * the user it was". If it is not, nothing is written and the screen re-asks.
   */
  const applySyncPlan = useCallback(
    async ({ shopSource, productId, plan }) => {
      if (!plan || !plan.hasChanges) {
        return { ok: true, changed: [], written: {}, noop: true };
      }

      const loadedValues = plan.changes.reduce(
        (values, change) => ({ ...values, [change.key]: change.from }),
        {},
      );

      return writeFields({
        shopSource,
        productId,
        patch: plan.values,
        loadedValues,
        fields: plan.changes.map((change) => change.key),
      });
    },
    [writeFields],
  );

  return {
    hasHvoSession,
    canWriteToShop,
    readFields,
    updateWarehouseFields,
    applySyncPlan,
    refreshProducts: revalidateProducts,
  };
};
